import PropTypes from "prop-types";
import React, { useState, useMemo, useCallback } from "react";
import Select from "react-select";
import styled, { useTheme } from "styled-components";
import { useDebounce } from "use-lodash-debounce";

import { get } from "lodash";
import SelectComponents from "src/components/SelectComponents";
import Option from "src/components/Search/sub-components/Option";
import NoOptionsMessage from "src/components/Search/sub-components/NoOptionsMessage";
import { useGlobalSearch } from "src/components/Search/globalSearch";
import {
  placeholderText,
  reactSelectStyles,
} from "src/components/Search/Search.config";
import WorkspacePropType from "src/custom-prop-types/workspace";
import URL, { url } from "src/consts/urlPaths";

const SEARCH_SIZE = 10;
const DEBOUNCE_MS = 400;

const SearchWrapper = styled.div`
  height: 45px;
  width: 100%;
  & [class$="-container"] {
    height: 100% !important;
  }
`;

const ReportTeamChangeSearch = ({
  entityType,
  onOptionSelect,
  workspace,
  isMulti = true,
  isCloseOnSelect = false,
  noOptionsComponent,
}) => {
  const theme = useTheme();
  const [inputValue, setInputValue] = useState("");
  const debouncedSearchTerm = useDebounce(inputValue, DEBOUNCE_MS);

  const includePeople = entityType === "people";
  const includeGroups = entityType === "teams";
  const customer = get(workspace, "config.customer");
  const tagTypesConfig = get(workspace, "config.tagConfig", {});
  const groupTypes = get(workspace, "config.groupTypes", {});

  const { loading, error, results } = useGlobalSearch({
    disabled: !debouncedSearchTerm,
    searchTerm: debouncedSearchTerm,
    size: SEARCH_SIZE,
    scope: null,
    peopleFilters: [],
    groupFilters: [],
    defaultTeamTypes: [],
    tagTypesConfig,
    tagsVisibleIn: "directory",
    includePeople,
    includeGroups,
    includeTags: false,
    customer,
  });

  const options = useMemo(() => {
    if (!results) {
      return [];
    }
    if (entityType === "people" && results.people?.options) {
      return results.people.options;
    }
    if (entityType === "teams" && results.teams?.options) {
      return results.teams.options;
    }
    return [];
  }, [entityType, results]);

  const CustomOption = useCallback(
    (props) => (
      <Option
        {...props}
        groupTypes={groupTypes}
        workspace={workspace}
        selectedString={inputValue}
        preventNavigation
      />
    ),
    [groupTypes, workspace, inputValue]
  );

  const components = useMemo(
    () => ({
      ...SelectComponents,
      Option: CustomOption,
      ClearIndicator: () => null,
    }),
    [CustomOption]
  );

  const onChange = useCallback(
    (value) => {
      if (onOptionSelect) {
        onOptionSelect(value || []);
      }
    },
    [onOptionSelect]
  );

  const onInputChange = useCallback((newValue) => {
    setInputValue(newValue);
    return newValue;
  }, []);

  const getOptionValue = (option) =>
    option?.value?.person?.aggregateId ||
    option?.value?.group?.id ||
    option?.value?.aggregateId ||
    "";

  return (
    <SearchWrapper>
      <Select
        inputValue={inputValue}
        onInputChange={onInputChange}
        onChange={onChange}
        options={options}
        isMulti={isMulti}
        closeMenuOnSelect={isCloseOnSelect}
        isLoading={loading}
        placeholder={placeholderText}
        noOptionsMessage={() =>
          noOptionsComponent ?? (
            <NoOptionsMessage
              searchError={error}
              label="Not sure what to search for?"
              link={
                entityType === "people"
                  ? {
                      url: url(URL.PEOPLE),
                      label: "Click here to go to people view",
                      openInNewTab: true,
                    }
                  : {
                      url: url(URL.TEAMS),
                      label: "Click here to go to teams view",
                    }
              }
            />
          )
        }
        filterOption={() => true}
        components={components}
        styles={reactSelectStyles(theme, 0, loading)}
        aria-label={`Search for ${entityType}`}
        getOptionValue={getOptionValue}
      />
    </SearchWrapper>
  );
};

ReportTeamChangeSearch.propTypes = {
  entityType: PropTypes.oneOf(["people", "teams"]).isRequired,
  onOptionSelect: PropTypes.func.isRequired,
  workspace: WorkspacePropType.isRequired,
  isMulti: PropTypes.bool,
  isCloseOnSelect: PropTypes.bool,
  noOptionsComponent: PropTypes.node,
};

export default ReportTeamChangeSearch;
